import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/i18n/I18nContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NewRequest from "@/pages/NewRequest";
import Requests from "@/pages/Requests";
import Producers from "@/pages/Producers";
import ProducerProfile from "@/pages/ProducerProfile";
import Availability from "@/pages/Availability";
import Community from "@/pages/Community";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Account from "@/pages/Account";
import Security from "@/pages/Security";
import Compare from "@/pages/Compare";
import RequestDetail from "@/pages/RequestDetail";
import ProposalForm from "@/pages/ProposalForm";
import ProposalDetail from "@/pages/ProposalDetail";
import MyRequests from "@/pages/MyRequests";
import MyProposals from "@/pages/MyProposals";
import Onboarding, { ONBOARDING_KEY } from "@/pages/Onboarding";

function Shell({ children }) { return <AppShell>{children}</AppShell>; }

/** Requires a Supabase session. Redirects to / when signed out.
 *  Also gates the app behind the 3-slide onboarding on very first launch. */
function RequireAuth({ children }) {
  const { session, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-500 font-mono text-xs">Loading…</div>;
  if (!session) return <Navigate to="/" replace />;
  try {
    const done = typeof window !== "undefined" && localStorage.getItem(ONBOARDING_KEY) === "true";
    if (!done && window.location.pathname !== "/app/onboarding") {
      return <Navigate to="/app/onboarding" replace />;
    }
  } catch { /* ignore */ }
  return children;
}

/** Wrapper for the onboarding route: requires a session, does NOT check the flag,
 *  and does NOT wrap in AppShell (full-bleed page). */
function OnboardingRoute() {
  const { session, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-500 font-mono text-xs">Loading…</div>;
  if (!session) return <Navigate to="/" replace />;
  return <Onboarding />;
}

/** Sends already-signed-in users away from the Login/Register pages.
 *  Renders children immediately so the login form is never blank during bootstrap. */
function RedirectIfAuthed({ children }) {
  const { session, loading } = useAuth();
  if (!loading && session) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
            <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
            <Route path="/app/onboarding" element={<OnboardingRoute />} />
            <Route path="/app/dashboard" element={<RequireAuth><Shell><Dashboard /></Shell></RequireAuth>} />
            <Route path="/app/new-request" element={<RequireAuth><Shell><NewRequest /></Shell></RequireAuth>} />
            <Route path="/app/requests" element={<RequireAuth><Shell><Requests /></Shell></RequireAuth>} />
            <Route path="/app/my-requests" element={<RequireAuth><Shell><MyRequests /></Shell></RequireAuth>} />
            <Route path="/app/my-proposals" element={<RequireAuth><Shell><MyProposals /></Shell></RequireAuth>} />
            <Route path="/app/requests/:ref" element={<RequireAuth><Shell><RequestDetail /></Shell></RequireAuth>} />
            <Route path="/app/requests/:ref/reply" element={<RequireAuth><Shell><ProposalForm /></Shell></RequireAuth>} />
            <Route path="/app/proposals/:id" element={<RequireAuth><Shell><ProposalDetail /></Shell></RequireAuth>} />
            <Route path="/app/producers" element={<RequireAuth><Shell><Producers /></Shell></RequireAuth>} />
            <Route path="/app/producers/:id" element={<RequireAuth><Shell><ProducerProfile /></Shell></RequireAuth>} />
            <Route path="/app/availability" element={<RequireAuth><Shell><Availability /></Shell></RequireAuth>} />
            <Route path="/app/community" element={<RequireAuth><Shell><Community /></Shell></RequireAuth>} />
            <Route path="/app/messages" element={<RequireAuth><Shell><Messages /></Shell></RequireAuth>} />
            <Route path="/app/notifications" element={<RequireAuth><Shell><Notifications /></Shell></RequireAuth>} />
            <Route path="/app/profile" element={<RequireAuth><Shell><Profile /></Shell></RequireAuth>} />
            <Route path="/app/account" element={<RequireAuth><Shell><Account /></Shell></RequireAuth>} />
            <Route path="/app/security" element={<RequireAuth><Shell><Security /></Shell></RequireAuth>} />
            <Route path="/app/compare" element={<RequireAuth><Shell><Compare /></Shell></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="light"
          position="top-right"
          toastOptions={{ style: { background: "#FFFFFF", border: "1px solid #D9CFB2", color: "#23211C" } }}
        />
      </I18nProvider>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
